import React, { useState, useEffect, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import "./AjustesAdmin.css";
import api from '../../config/axiosConfig';
import Permiso from "../../router/Permiso";
import { EmpresaService } from "../../services/EmpresaService";
import { TipificacionService } from "../../services/TipificacionService";
import { CategoriaService } from "../../services/CategoriaService";
import { PerfilService } from "../../services/PerfilService";
import { toast } from "react-toastify";
import AsignarPermisos from "../permisos/AsignarPermisos";
import { ticketService } from "../../services/TicketService";
import { AuthContext } from "../../context/AuthContext";
import { getBaseUrl } from "../../config/api";

const AjustesAdmin = () => {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("empresa");
  const [loading, setLoading] = useState(false);
  const [error,setError] = useState("");
  const [success, setSuccess] = useState("");
  const [empresaViewMode, setEmpresaViewMode] = useState("form"); // 'form' o 'table'
  const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);
  const [showPermisoModal, setShowPermisoModal] = useState(false);

  // Estados para cada sección
  const [empresaData, setEmpresaData] = useState({
    nombre: "",
    ruc: "",
    direccion: "",
    telefono: "",
    email: "",
    website: "",
    logo_login: "",
    icon: "",
    logo_dashboard: "",
    name_app: "",
    login_background: "",
  });

  const [logoLoginFile, setLogoLoginFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [logoDashboardFile, setLogoDashboardFile] = useState(null);
  const [logoLoginPreview, setLogoLoginPreview] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [logoDashboardPreview, setLogoDashboardPreview] = useState(null);
  const [loginBackgroundFile, setLoginBackgroundFile] = useState(null);
  const [loginBackgroundPreview, setLoginBackgroundPreview] = useState(null);

  const [tipificaciones, setTipificaciones] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [tipoSolicitud, setTipoSolicitud] = useState([]);

  // Estados para formularios
  const [showTipificacionModal, setShowTipificacionModal] = useState(false);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [showTipoSolicitudModal, setShowTipoSolicitudModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const baseUrl = getBaseUrl();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    let data;
    try {
      switch (activeTab) {
        case "empresa":
          data = await EmpresaService.fetchEmpresa(token);
          setEmpresaData(data);
          if (data.logo_login) {
            setLogoLoginPreview(`${baseUrl}/uploads/logos/${data.logo_login}`);
          }
          if (data.icon) {
            setIconPreview(`${baseUrl}/uploads/logos/${data.icon}`);
          }
          if (data.logo_dashboard) {
            setLogoDashboardPreview(`${baseUrl}/uploads/logos/${data.logo_dashboard}`);
          }
          if (data.login_background) {
            setLoginBackgroundPreview(`${baseUrl}/uploads/logos/${data.login_background}`);
          }
          break;
        case "tipificaciones":
          data = await TipificacionService.fetchTipificaciones(token);
          setTipificaciones(data);
          break;
        case "categorias":
          data = await CategoriaService.getCategorias(token);
          setCategorias(data);
          break;
        case "perfiles":
          data = await PerfilService.fetchPerfiles(token);
          setPerfiles(data);
          break;
        case "solicitudes":
          data = await ticketService.getTiposSolicitud(token);
          setTipoSolicitud(data);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("Error en fetchData:", err);
      if (err.response?.status === 500) {
        setError(
          "Error del servidor. Verifique que las tablas de la base de datos existan."
        );
      } else if (err.response?.status === 401) {
        setError(
          "Error de autenticación. Por favor, inicie sesión nuevamente."
        );
      } else if (err.code === "ECONNREFUSED") {
        setError(
          "No se puede conectar al servidor. Verifique que el backend esté ejecutándose."
        );
      } else {
        setError("Error al cargar datos. Intente nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, token, baseUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEmpresaSave = async () => {
    try {
      const formData = new FormData();

      formData.append("nombre", empresaData.nombre || "");
      formData.append("ruc", empresaData.ruc || "");
      formData.append("direccion", empresaData.direccion || "");
      formData.append("telefono", empresaData.telefono || "");
      formData.append("email", empresaData.email || "");
      formData.append("website", empresaData.website || "");
      formData.append("name_app", empresaData.name_app || "");

      if (logoLoginFile) {
        formData.append("logo_login", logoLoginFile);
      }
      if (iconFile) {
        formData.append("icon", iconFile);
      }
      if (logoDashboardFile) {
        formData.append("logo_dashboard", logoDashboardFile);
      }

      if (loginBackgroundFile) {
        formData.append("login_background", loginBackgroundFile);
      }

      await EmpresaService.actualizarEmpresa({ token, empresaData: formData });

      setSuccess("Datos de empresa actualizados correctamente");
      setEmpresaViewMode("table");
    } catch (err) {
      console.error(err);
      setError("Error al actualizar datos de empresa");
    }
  };

  const handleLogoLoginChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoLoginFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoLoginPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoDashboardChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoDashboardFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoDashboardPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLoginBackgroundChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoginBackgroundFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLoginBackgroundPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleTipificacionSave = async (formData) => {
    try {
      await TipificacionService.guardarTipificacion({ token, formData, editingItem });

      setShowTipificacionModal(false);
      setEditingItem(null);
      fetchData();
      toast.success("Tipificación guardada correctamente");
    } catch (err) {
      console.error(err);
      setError("Error al guardar tipificación");
    }
  };

  const handleTipoSolicitudSave = async (formData) => {
    try {
      await ticketService.guardarTipoSolicitud({ token, formData, editingItem });

      setShowTipoSolicitudModal(false);
      setEditingItem(null);
      fetchData();
      toast.success("Tipo de Solicitud guardada correctamente");
    } catch (err) {
      console.error(err);
      setError("Error al guardar Tipo de Solicitud");
    }
  };

  const handleCategoriaSave = async (formData) => {
    try {
      await CategoriaService.guardarCategoria({ token, formData, editingItem });

      setShowCategoriaModal(false);
      setEditingItem(null);
      fetchData();
      toast.success("Categoría guardada correctamente");
    } catch (err) {
      console.error(err);
      setError("Error al guardar categoría");
    }
  };

  const handlePerfilSave = async (formData) => {
    try {
      await PerfilService.guardarPerfil({ token, formData, editingItem });

      setShowPerfilModal(false);
      setEditingItem(null);
      fetchData();
      toast.success("Perfil guardado correctamente");
    } catch (err) {
      console.error(err);
      setError("Error al guardar perfil");
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm?.("¿Está seguro de eliminar este elemento?")) {
      return;
    }

    try {
      await api.delete(`/ajustes/${type}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
      toast.success("Elemento eliminado correctamente");
    } catch (err) {
      console.error(err);
      setError("Error al eliminar elemento");
    }
  };

  const handleDeleteTipoSolicitud = async (type, id) => {
    if (!window?.confirm("¿Está seguro de eliminar este elemento?")) {
      return;
    }

    try {
      await api.delete(`/tickets/tiposolicitud/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
      toast.success("Elemento eliminado correctamente");
    } catch (err) {
      console.error(err);
      setError("Error al eliminar elemento");
    }
  };

  // Función para renderizar la vista de tabla de empresa
  const renderEmpresaTableView = () => (
    <div className="empresa-table-view">
      <table className="empresa-table">
        <thead>
          <tr>
            <th>Campo</th>
            <th>Valor</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong> Nombre de la Empresa</strong>
            </td>
            <td>{empresaData.nombre || "No especificado"}</td>
          </tr>
          <tr>
            <td>
              <strong> RUC</strong>
            </td>
            <td>{empresaData.ruc || "No especificado"}</td>
          </tr>
          <tr>
            <td>
              <strong> Dirección</strong>
            </td>
            <td>{empresaData.direccion || "No especificado"}</td>
          </tr>
          <tr>
            <td>
              <strong> Teléfono</strong>
            </td>
            <td>{empresaData.telefono || "No especificado"}</td>
          </tr>
          <tr>
            <td>
              <strong> Email</strong>
            </td>
            <td>{empresaData.email || "No especificado"}</td>
          </tr>
          <tr>
            <td>
              <strong> Sitio Web</strong>
            </td>
            <td>{empresaData.website || "No especificado"}</td>
          </tr>
          <tr>
            <td>
              <strong> Logo Login</strong>
            </td>
            <td>
              {empresaData.logo_login ? (
                <img
                  src={`${baseUrl}/uploads/logos/${empresaData.logo_login}`}
                  alt="Logo Login"
                  style={{ maxWidth: "150px", maxHeight: "50px", objectFit: "contain" }}
                />
              ) : (
                "Sin imagen"
              )}
            </td>
          </tr>
          <tr>
            <td>
              <strong> Icono</strong>
            </td>
            <td>
              {empresaData.icon ? (
                <img
                  src={`${baseUrl}/uploads/logos/${empresaData.icon}`}
                  alt="Icon"
                  style={{ maxWidth: "50px", maxHeight: "50px", objectFit: "contain" }}
                />
              ) : (
                "Sin imagen"
              )}
            </td>
          </tr>
          <tr>
            <td>
              <strong> Logo Dashboard</strong>
            </td>
            <td>
              {empresaData.logo_dashboard ? (
                <img
                  src={`${baseUrl}/uploads/logos/${empresaData.logo_dashboard}`}
                  alt="Logo Dashboard"
                  style={{ maxWidth: "150px", maxHeight: "50px", objectFit: "contain" }}
                />
              ) : (
                "Sin imagen"
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderEmpresaFormView = () => (
    <div className="empresa-section">
      <h3>Datos de la Empresa</h3>
      <div className="form-grid-empresa">
        <div className="form-group" style={{ gridArea: "nombre" }}>
          <label htmlFor="empresaNombre">Nombre de la Empresa</label>
          <input
            id="empresaNombre"
            type="text"
            value={empresaData.nombre || ""}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, nombre: e.target.value })
            }
            placeholder="Ingrese el nombre de la empresa"
          />
        </div>
        <div className="form-group" style={{ gridArea: "ruc" }}>
          <label htmlFor="empresaRuc">RUC</label>
          <input
            id="empresaRuc"
            type="text"
            value={empresaData.ruc || ""}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, ruc: e.target.value })
            }
            placeholder="Ingrese el RUC"
          />
        </div>
        <div className="form-group" style={{ gridArea: "direccion" }}>
          <label htmlFor="empresaDireccion">Dirección</label>
          <input
            id="empresaDireccion"
            type="text"
            value={empresaData.direccion || ""}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, direccion: e.target.value })
            }
            placeholder="Ingrese la dirección"
          />
        </div>
        <div className="form-group" style={{ gridArea: "telefono" }}>
          <label htmlFor="empresaTelefono">Teléfono</label>
          <input
            id="empresaTelefono"
            type="text"
            value={empresaData.telefono || ""}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, telefono: e.target.value })
            }
            placeholder="Ingrese el teléfono"
          />
        </div>
        <div className="form-group" style={{ gridArea: "email" }}>
          <label htmlFor="empresaEmail">Email</label>
          <input
            id="empresaEmail"
            type="email"
            value={empresaData.email || ""}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, email: e.target.value })
            }
            placeholder="Ingrese el email"
          />
        </div>
        <div className="form-group" style={{ gridArea: "website" }}>
          <label htmlFor="empresaWebsite">Sitio Web</label>
          <input
            id="empresaWebsite"
            type="url"
            value={empresaData.website || ""}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, website: e.target.value })
            }
            placeholder="Ingrese el sitio web"
          />
        </div>

        <div className="form-group image-upload-group" style={{ gridArea: "logoLogin" }}>
          <label htmlFor="logoLoginInput">Logo Login</label>
          <div className="image-upload-container">
            <input
              id="logoLoginInput"
              type="file"
              accept="image/*"
              onChange={handleLogoLoginChange}
              className="image-upload-input"
            />
            {logoLoginPreview && (
              <div className="image-preview-logo">
                <img src={logoLoginPreview} alt="Logo Login Preview" />
                <button
                  className="image-remove-btn"
                  onClick={() => {
                    setLogoLoginPreview(null);
                    setLogoLoginFile(null);
                    setEmpresaData({ ...empresaData, logo_login: null });
                  }}
                >
                  ×
                </button>
              </div>
            )}
            {!logoLoginPreview && (
              <div className="image-placeholder">
                {empresaData.logo_login ? (
                  <img src={empresaData.logo_login} alt="Logo Login" />
                ) : (
                  <span>Sin imagen</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group image-upload-group" style={{ gridArea: "logoIcon" }}>
          <label htmlFor="iconoInput">Icono</label>
          <div className="image-upload-container">
            <input
              id="iconoInput"
              type="file"
              accept="image/*"
              onChange={handleIconChange}
              className="image-upload-input"
            />
            {iconPreview && (
              <div className="image-preview-logo">
                <img src={iconPreview} alt="Icon Preview" />
                <button
                  className="image-remove-btn"
                  onClick={() => {
                    setIconPreview(null);
                    setIconFile(null);
                    setEmpresaData({ ...empresaData, icon: null });
                  }}
                >
                  ×
                </button>
              </div>
            )}
            {!iconPreview && (
              <div className="image-placeholder">
                {empresaData.icon ? (
                  <img src={empresaData.icon} alt="Icon" />
                ) : (
                  <span>Sin imagen</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group image-upload-group" style={{ gridArea: "logoDash" }}>
          <label htmlFor="logoDashInput">Logo Dashboard</label>
          <div className="image-upload-container">
            <input
              id="logoDashInput"
              type="file"
              accept="image/*"
              onChange={handleLogoDashboardChange}
              className="image-upload-input"
            />
            {logoDashboardPreview && (
              <div className="image-preview-logo">
                <img src={logoDashboardPreview} alt="Logo Dashboard Preview" />
                <button
                  className="image-remove-btn"
                  onClick={() => {
                    setLogoDashboardPreview(null);
                    setLogoDashboardFile(null);
                    setEmpresaData({ ...empresaData, logo_dashboard: null });
                  }}
                >
                  ×
                </button>
              </div>
            )}
            {!logoDashboardPreview && (
              <div className="image-placeholder">
                {empresaData.logo_dashboard ? (
                  <img src={empresaData.logo_dashboard} alt="Logo Dashboard" />
                ) : (
                  <span>Sin imagen</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="form-group" style={{ gridArea: "name_app" }}>
          <label htmlFor="nameApp" className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Aplicación</label>
          <input
            id="nameApp"
            type="text"
            name="name_app"
            value={empresaData.name_app}
            onChange={(e) =>
              setEmpresaData({ ...empresaData, name_app: e.target.value })
            }

          />
        </div>
        <div className="form-group image-upload-group" style={{ gridArea: "loginBg" }}>
          <label htmlFor="loginBgInput">Fondo de Pantalla Login</label>
          <div className="image-upload-container">
            <input
              id="loginBgInput"
              type="file"
              accept="image/*"
              onChange={handleLoginBackgroundChange}
              className="image-upload-input"
            />
            {loginBackgroundPreview && (
              <div className="image-preview-logo">
                <img src={loginBackgroundPreview} alt="Fondo Login Preview" />
                <button
                  className="image-remove-btn"
                  onClick={() => {
                    setLoginBackgroundPreview(null);
                    setLoginBackgroundFile(null);
                    setEmpresaData({ ...empresaData, login_background: null });
                  }}
                >
                  ×
                </button>
              </div>
            )}
            {!loginBackgroundPreview && (
              <div className="image-placeholder">
                {empresaData.login_background ? (
                  <img src={empresaData.login_background} alt="Fondo Login" />
                ) : (
                  <span>Sin imagen</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <button className="btn-save" onClick={handleEmpresaSave}>
        Guardar Datos
      </button>
    </div>
  );

  return (
    <div className="usuarios-admin-outer">
      <div className="usuarios-admin-header">
        <div className="usuarios-admin-header-row">
          <div>
            <div className="usuarios-admin-title">Ajustes</div>
            <div className="usuarios-admin-subtitle">
              Gestión profesional de ajustes
            </div>
          </div>
        </div>
        <div className="usuarios-admin-tabs">
          <Permiso permiso="manage_company">
            <button
              className={activeTab === "empresa" ? "active" : ""}
              onClick={() => setActiveTab("empresa")}
            >
              Datos de Empresa
            </button>
          </Permiso>
          <Permiso permiso="list_tipificacion">
            <button
              className={activeTab === "tipificaciones" ? "active" : ""}
              onClick={() => setActiveTab("tipificaciones")}
            >
              Tipificaciones
            </button>
          </Permiso>
          <Permiso permiso="list_category">
            <button
              className={activeTab === "categorias" ? "active" : ""}
              onClick={() => setActiveTab("categorias")}
            >
              Categorías Productos
            </button>
          </Permiso>
          <Permiso permiso="list_perfiles">
            <button
              className={activeTab === "perfiles" ? "active" : ""}
              onClick={() => setActiveTab("perfiles")}
            >
              Perfiles
            </button>
          </Permiso>
          <Permiso permiso="list_perfiles">
            <button
              className={activeTab === "solicitudes" ? "active" : ""}
              onClick={() => setActiveTab("solicitudes")}
            >
              Tipos de Solicitud
            </button>
          </Permiso>
        </div>
      </div>
      <div id="ajustes-container" className="ajustes-admin-container">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="ajustes-content">
          <Permiso permiso="manage_company">
            {activeTab === "empresa" && (
              <div>
                {/* Toggle para cambiar entre vista de formulario y tabla */}
                <div className="view-toggle">
                  <button
                    className={empresaViewMode === "form" ? "active" : ""}
                    onClick={() => setEmpresaViewMode("form")}
                  >
                    Formulario
                  </button>
                  <button
                    className={empresaViewMode === "table" ? "active" : ""}
                    onClick={() => setEmpresaViewMode("table")}
                  >
                    Tabla
                  </button>
                </div>

                {empresaViewMode === "form"
                  ? renderEmpresaFormView()
                  : renderEmpresaTableView()}
              </div>
            )}
          </Permiso>

          {activeTab === "tipificaciones" && (
            <div className="tipificaciones-section">
              <div className="section-header">
                <h3>Tipificaciones</h3>
                <Permiso permiso="add_tipificacion">
                  <button
                    className="btn-add"
                    onClick={() => {
                      setEditingItem(null);
                      setShowTipificacionModal(true);
                    }}
                  >
                    + Nueva Tipificación
                  </button>
                </Permiso>
              </div>
              {loading ? (
                <div className="loading">Cargando tipificaciones...</div>
              ) : (
                <div className="items-grid">
                  {tipificaciones
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map((item) => (
                      <div key={item.id} className="item-card">
                        <h4>{item.nombre}</h4>
                        <p>{item.descripcion}</p>
                        <div className="item-actions">
                          <Permiso permiso="edit_tipificacion">
                            <button
                              className="btn-edit"
                              onClick={() => {
                                setEditingItem(item);
                                setShowTipificacionModal(true);
                              }}
                            >
                              Editar
                            </button>
                          </Permiso>
                          <Permiso permiso="delete_tipificacion">
                            <button
                              className="btn-delete"
                              onClick={() =>
                                handleDelete("tipificaciones", item.id)
                              }
                            >
                              Eliminar
                            </button>
                          </Permiso>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "categorias" && (
            <div className="categorias-section">
              <div className="section-header">
                <h3>Categorías de Productos</h3>
                <Permiso permiso="add_category">
                  <button
                    className="btn-add"
                    onClick={() => {
                      setEditingItem(null);
                      setShowCategoriaModal(true);
                    }}
                  >
                    + Nueva Categoría
                  </button>
                </Permiso>
              </div>
              {loading ? (
                <div className="loading">Cargando categorías...</div>
              ) : (
                <div className="items-grid">
                  {categorias
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map((item) => (
                      <div key={item.id} className="item-card">
                        <h4>{item.nombre}</h4>
                        <p>{item.descripcion}</p>
                        <div className="item-actions">
                          <Permiso permiso="edit_category">
                            <button
                              className="btn-edit"
                              onClick={() => {
                                setEditingItem(item);
                                setShowCategoriaModal(true);
                              }}
                            >
                              Editar
                            </button>
                          </Permiso>
                          <Permiso permiso="delete_category">
                            <button
                              className="btn-delete"
                              onClick={() =>
                                handleDelete("categorias", item.id)
                              }
                            >
                              Eliminar
                            </button>
                          </Permiso>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "perfiles" && (
            <div className="perfiles-section">
              <div className="section-header">
                <h3>Perfiles de Usuario</h3>
                <Permiso permiso="add_perfiles">
                  <button
                    className="btn-add"
                    onClick={() => {
                      setEditingItem(null);
                      setShowPerfilModal(true);
                    }}
                  >
                    + Nuevo Perfil
                  </button>
                </Permiso>
              </div>
              {loading ? (
                <div className="loading">Cargando perfiles...</div>
              ) : (
                <div className="items-grid">
                  {perfiles
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map((item) => (
                      <div key={item.id} className="item-card">
                        <h4>{item.nombre}</h4>
                        <p>{item.descripcion}</p>
                        <div className="item-actions">
                          <Permiso permiso="perfil_asignar_permiso">
                            <button
                              className="btn-edit"
                              onClick={() => {
                                setPerfilSeleccionado(item);
                                setShowPermisoModal(true);
                              }}
                            >
                              Asignar permisos
                            </button>
                          </Permiso>
                          <Permiso permiso="edit_perfiles">
                            <button
                              className="btn-edit"
                              onClick={() => {
                                setEditingItem(item);
                                setShowPerfilModal(true);
                              }}
                            >
                              Editar
                            </button>
                          </Permiso>
                          <Permiso permiso="delete_perfiles">
                            <button
                              className="btn-delete"
                              onClick={() => handleDelete("perfiles", item.id)}
                            >
                              Eliminar
                            </button>
                          </Permiso>
                        </div>
                      </div>
                    ))}
                </div>
              )}

            </div>
          )}

          {activeTab === "solicitudes" && (
            <div className="tipificaciones-section">
              <div className="section-header">
                <h3>Tipos de Solicitud</h3>
                <Permiso permiso="add_tipificacion">
                  <button
                    className="btn-add"
                    onClick={() => {
                      setEditingItem(null);
                      setShowTipoSolicitudModal(true);
                    }}
                  >
                    + Nuevo Tipo de Solicitud
                  </button>
                </Permiso>
              </div>
              {loading ? (
                <div className="loading">Cargando tipo de solicitudes...</div>
              ) : (
                <div className="items-grid">
                  {tipoSolicitud
                    .sort((a, b) => a.descripcion.localeCompare(b.descripcion))
                    .map((item) => (
                      <div key={item.id} className="item-card">
                        <h4>{item.descripcion}</h4>
                        <div className="item-actions">
                          <Permiso permiso="edit_tipificacion">
                            <button
                              className="btn-edit"
                              onClick={() => {
                                setEditingItem(item);
                                setShowTipoSolicitudModal(true);
                              }}
                            >
                              Editar
                            </button>
                          </Permiso>
                          <Permiso permiso="delete_tipificacion">
                            <button
                              className="btn-delete"
                              onClick={() =>
                                handleDeleteTipoSolicitud("tiposolicitud", item.id)
                              }
                            >
                              Eliminar
                            </button>
                          </Permiso>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        {showPermisoModal && (
          <AsignarPermisos perfilId={perfilSeleccionado.id} perfilName={perfilSeleccionado.nombre} onClose={() => setShowPermisoModal(false)} token={token} />
        )}

        {/* Modales */}
        {showTipificacionModal && (
          <TipificacionModal
            item={editingItem}
            onSave={handleTipificacionSave}
            onClose={() => {
              setShowTipificacionModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showTipoSolicitudModal && (
          <TipoSolicitudModal
            item={editingItem}
            onSave={handleTipoSolicitudSave}
            onClose={() => {
              setShowTipoSolicitudModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showCategoriaModal && (
          <CategoriaModal
            item={editingItem}
            onSave={handleCategoriaSave}
            onClose={() => {
              setShowCategoriaModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showPerfilModal && (
          <PerfilModal
            item={editingItem}
            onSave={handlePerfilSave}
            onClose={() => {
              setShowPerfilModal(false);
              setEditingItem(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Componentes modales
const TipificacionModal = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: item?.nombre || "",
    descripcion: item?.descripcion || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <span>{item ? "Editar" : "Nueva"} Tipificación</span>
        </div>
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="tipifNombre">Nombre</label>
              <input
                id="tipifNombre"
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="tipifDescripcion">Descripción</label>
              <textarea
                id="tipifDescripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                rows="3"
              />
            </div>
            <div className="modal-buttons-compact">
              <button type="submit" className="save-btn">Guardar</button>
              <button type="button" className="cancel-btn" onClick={onClose}>Cancelar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

TipificacionModal.propTypes = { item: PropTypes.object, onSave: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

const TipoSolicitudModal = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    descripcion: item?.descripcion || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <span>{item ? "Editar" : "Nuevo"} Tipo de Solicitud</span>
        </div>
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="tipoSolDescripcion">Descripción</label>
              <input
                id="tipoSolDescripcion"
                type="text"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                required
              />
            </div>
            <div className="modal-buttons-compact">
              <button type="submit" className="save-btn">Guardar</button>
              <button type="button" className="cancel-btn" onClick={onClose}>Cancelar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

TipoSolicitudModal.propTypes = { item: PropTypes.object, onSave: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

const CategoriaModal = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: item?.nombre || "",
    descripcion: item?.descripcion || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <span>{item ? "Editar" : "Nueva"} Categoría</span>
        </div>
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="catNombre">Nombre</label>
              <input
                id="catNombre"
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="catDescripcion">Descripción</label>
              <textarea
                id="catDescripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                rows="3"
              />
            </div>
            <div className="modal-buttons-compact">
              <button type="submit" className="save-btn">Guardar</button>
              <button type="button" className="cancel-btn" onClick={onClose}>Cancelar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

CategoriaModal.propTypes = { item: PropTypes.object, onSave: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

const PerfilModal = ({ item, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: item?.nombre || "",
    descripcion: item?.descripcion || "",
    permisos: item?.permisos || [],
    web: item?.web === 1,
    movil: item?.movil === 1
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <span>{item ? "Editar" : "Nuevo"} Perfil</span>
        </div>
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="perfilNombre">Nombre</label>
              <input
                id="perfilNombre"
                type="text"
                value={formData.nombre}
                onChange={(e) =>
                  setFormData({ ...formData, nombre: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="perfilDescripcion">Descripción</label>
              <textarea
                id="perfilDescripcion"
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                rows="3"
              />
            </div>

            <div className="form-group checkboxes-group">
              <div className="checkbox-item">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.web || false}
                    onChange={(e) =>
                      setFormData({ ...formData, web: e.target.checked })
                    }
                  />
                  Web
                </label>
              </div>
              <div className="checkbox-item">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.movil || false}
                    onChange={(e) =>
                      setFormData({ ...formData, movil: e.target.checked })
                    }
                  />
                  Móvil
                </label>
              </div>
            </div>
            <div className="modal-buttons-compact">
              <button type="submit" className="save-btn">
                Guardar
              </button>
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancelar
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

PerfilModal.propTypes = { item: PropTypes.object, onSave: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

export default AjustesAdmin;
